import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import {
  listVolunteers,
  createVolunteer,
  updateVolunteer,
  deleteVolunteer,
  type VolunteerWrite,
} from "@/lib/volunteers-api";
import { sanitizePhone, isValidPhone, PHONE_INPUT_PROPS } from "@/lib/phone";

type V = {
  id?: string;
  name: string;
  phone: string;
  languages: string[];
  experience_years: number;
  charges_per_day: number;
  bio_en: string;
  bio_hi: string;
  bio_gu: string;
  active: boolean;
};
const EMPTY: V = {
  name: "",
  phone: "",
  languages: ["English"],
  experience_years: 0,
  charges_per_day: 500,
  bio_en: "",
  bio_hi: "",
  bio_gu: "",
  active: true,
};

export const Route = createFileRoute("/admin/volunteers")({ component: VolPage });

function VolPage() {
  const qc = useQueryClient();
  const [edit, setEdit] = useState<V | null>(null);

  const {
    data: list = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["jgy-volunteers"],
    queryFn: listVolunteers,
  });

  const toWrite = (d: V): VolunteerWrite => ({
    name: d.name,
    phone: d.phone,
    languages: d.languages,
    experience_years: d.experience_years,
    charges_per_day: d.charges_per_day,
    bio_en: d.bio_en,
    bio_hi: d.bio_hi,
    bio_gu: d.bio_gu,
    active: d.active,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["jgy-volunteers"] });
  const onError = (e: unknown) =>
    toast.error(e instanceof Error ? e.message : "Something went wrong");

  const createMut = useMutation({
    mutationFn: (d: V) => createVolunteer(toWrite(d)),
    onSuccess: () => {
      invalidate();
      toast.success("Volunteer created");
      setEdit(null);
    },
    onError,
  });
  const updateMut = useMutation({
    mutationFn: (d: V) => updateVolunteer(d.id as string, toWrite(d)),
    onSuccess: () => {
      invalidate();
      toast.success("Volunteer updated");
      setEdit(null);
    },
    onError,
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteVolunteer(id),
    onSuccess: () => {
      invalidate();
      toast.success("Deleted");
    },
    onError,
  });

  const saving = createMut.isPending || updateMut.isPending;

  const save = () => {
    if (!edit) {
      return;
    }
    if (!isValidPhone(edit.phone)) {
      toast.error("Enter a valid 10-digit phone number");
      return;
    }
    if (edit.id) {
      updateMut.mutate(edit);
    } else {
      createMut.mutate(edit);
    }
  };
  const del = (id: string) => {
    if (confirm("Delete?")) {
      deleteMut.mutate(id);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-2xl font-bold">Volunteers</h2>
          <p className="text-sm text-muted-foreground">Yatra guide volunteers</p>
        </div>
        <button onClick={() => setEdit({ ...EMPTY })} className="btn-primary py-2 text-sm">
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>
      <div className="-mx-4 overflow-hidden rounded-2xl border border-border bg-card sm:mx-0">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="p-3">Name</th>
                <th className="p-3">Phone</th>
                <th className="p-3">Languages</th>
                <th className="p-3">Experience</th>
                <th className="p-3">Charges/day</th>
                <th className="p-3">Status</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-sm text-muted-foreground">
                    Loading volunteers…
                  </td>
                </tr>
              )}
              {isError && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-sm text-destructive">
                    {error instanceof Error ? error.message : "Failed to load volunteers"}
                  </td>
                </tr>
              )}
              {!isLoading && !isError && list.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-sm text-muted-foreground">
                    No volunteers yet
                  </td>
                </tr>
              )}
              {list.map((v) => (
                <tr key={v.id} className="border-t border-border hover:bg-muted/40">
                  <td className="p-3 font-semibold">{v.name}</td>
                  <td className="p-3 text-muted-foreground">{v.phone || "—"}</td>
                  <td className="p-3 text-muted-foreground">{v.languages?.join(", ") || "—"}</td>
                  <td className="p-3 text-muted-foreground whitespace-nowrap">
                    {v.experience_years} yrs
                  </td>
                  <td className="p-3 text-muted-foreground whitespace-nowrap">
                    ₹{v.charges_per_day}
                  </td>
                  <td className="p-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${v.active ? "bg-green-100 text-green-700" : "bg-muted text-muted-foreground"}`}
                    >
                      {v.active ? "Active" : "Off"}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setEdit(v)}
                        title="Edit"
                        className="rounded-lg p-1.5 hover:bg-muted"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => v.id && del(v.id)}
                        title="Delete"
                        className="rounded-lg p-1.5 text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {edit && (
        <Editor
          v={edit}
          setV={setEdit}
          onSave={save}
          onClose={() => setEdit(null)}
          saving={saving}
        />
      )}
    </div>
  );
}

function Editor({
  v,
  setV,
  onSave,
  onClose,
  saving,
}: {
  v: V;
  setV: (x: V) => void;
  onSave: () => void;
  onClose: () => void;
  saving: boolean;
}) {
  const u = (k: keyof V, val: any) => setV({ ...v, [k]: val });
  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto jgy-overlay-scrim p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="mx-auto my-8 w-full max-w-2xl rounded-2xl bg-card p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-serif text-xl font-bold">{v.id ? "Edit" : "Add"} Volunteer</h3>
          <button onClick={onClose}>
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <F label="Name">
            <input className="inp" value={v.name} onChange={(e) => u("name", e.target.value)} />
          </F>
          <F label="Phone">
            <input
              {...PHONE_INPUT_PROPS}
              required
              className="inp"
              value={v.phone}
              onChange={(e) => u("phone", sanitizePhone(e.target.value))}
            />
          </F>
          <F label="Languages (comma)">
            <input
              className="inp"
              value={v.languages?.join(", ") || ""}
              onChange={(e) =>
                u(
                  "languages",
                  e.target.value
                    .split(",")
                    .map((x) => x.trim())
                    .filter(Boolean),
                )
              }
            />
          </F>
          <F label="Experience (years)">
            <input
              type="number"
              className="inp"
              value={v.experience_years}
              onChange={(e) => u("experience_years", +e.target.value)}
            />
          </F>
          <F label="Charges per day (₹)">
            <input
              type="number"
              className="inp"
              value={v.charges_per_day}
              onChange={(e) => u("charges_per_day", +e.target.value)}
            />
          </F>
          <F label="Active">
            <select
              className="inp"
              value={String(v.active)}
              onChange={(e) => u("active", e.target.value === "true")}
            >
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </F>
          <F label="Bio (EN)" full>
            <textarea
              rows={2}
              className="inp"
              value={v.bio_en}
              onChange={(e) => u("bio_en", e.target.value)}
            />
          </F>
          <F label="Bio (HI)" full>
            <textarea
              rows={2}
              className="inp"
              value={v.bio_hi}
              onChange={(e) => u("bio_hi", e.target.value)}
            />
          </F>
          <F label="Bio (GU)" full>
            <textarea
              rows={2}
              className="inp"
              value={v.bio_gu}
              onChange={(e) => u("bio_gu", e.target.value)}
            />
          </F>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} disabled={saving} className="btn-outline py-2 text-sm">
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="btn-primary py-2 text-sm disabled:pointer-events-none disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
      <style>{`.inp{width:100%;border:1px solid hsl(var(--border));background:hsl(var(--background));border-radius:.75rem;padding:.5rem .75rem;font-size:.875rem;outline:none}`}</style>
    </div>
  );
}
function F({
  label,
  full,
  children,
}: {
  label: string;
  full?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="text-[10px] font-semibold uppercase text-muted-foreground">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
